import { Button } from "@/components/ui/buttons/button";
import { useSPAContext, Intent } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { MotionItem, MotionStagger } from "@/motion/motion-primitives";

type HomeBottomNavProps = {
  /** Play the full landing wave (only true on Home's first arrival this session). */
  playIntro?: boolean;
  /** Seconds before the first button starts its wave. */
  navStart?: number;
  /** Seconds between each button's entrance. */
  navStagger?: number;
};

export const HomeBottomNav = ({
  playIntro = false,
  navStart = 0,
  navStagger = 0.07,
}: HomeBottomNavProps) => {
  const { setCurrentRoute, setIntent, setSelectedPokemons } = useSPAContext();

  const handleRankClick = () => {
    setIntent(undefined);
    setCurrentRoute(RouteNames.LEADERBOARD);
  };

  return (
    <nav className="w-full z-50 px-2 py-2 shadow-2xl relative">
      <div className="absolute bottom-0 left-0 w-full h-16 bg-pk-gray-1 z-[1]"></div>
      <MotionStagger
        stagger={playIntro ? navStagger : 0}
        delay={playIntro ? navStart : 0}
        className="grid grid-cols-5 items-center gap-1 z-[2] relative"
      >
        <BottomNavButton
          playIntro={playIntro}
          icon="poke"
          text="Pokémon"
          onClick={() => {
            setIntent(undefined);
            setCurrentRoute(RouteNames.INVENTORY_POKE);
          }}
        />

        <BottomNavButton
          playIntro={playIntro}
          icon="boxes/card"
          text="Decks"
          onClick={() => {
            setIntent(undefined);
            setCurrentRoute(RouteNames.ITEM_DECK_LIST);
          }}
        />

        <BottomNavButton
          playIntro={playIntro}
          icon="boxes/fighting"
          text="Train"
          onClick={() => {
            setIntent(Intent.SELECTING_POKEMONS_TO_TRAIN);
            setSelectedPokemons([]);
            setCurrentRoute(RouteNames.INVENTORY_POKE);
          }}
        />

        <BottomNavButton
          playIntro={playIntro}
          icon="shop"
          text="Merchant"
          onClick={() => {
            setIntent(undefined);
            setCurrentRoute(RouteNames.SHOP);
          }}
        />

        <BottomNavButton
          playIntro={playIntro}
          icon="medal"
          text="Rank"
          onClick={handleRankClick}
        />
      </MotionStagger>
    </nav>
  );
};

type BottomNavButtonProps = {
  icon: string;
  text: string;
  onClick: () => void;
  playIntro: boolean;
  /** Injected by the parent `MotionStagger` based on button index. */
  delay?: number;
};

const BottomNavButton = ({
  icon,
  text,
  onClick,
  playIntro,
  delay,
}: BottomNavButtonProps) => {
  return (
    <MotionItem
      variant={playIntro ? "popUp" : "fadeFast"}
      delay={playIntro ? delay : undefined}
    >
      <Button
        variant="default"
        onClick={onClick}
        className="w-full active:scale-95 transition-transform"
      >
        <div className="flex flex-col items-center justify-center gap-1 py-0.5">
          <img
            src={`/assets/icons/${icon}.${icon.startsWith("boxes/") ? "svg" : "png"}`}
            width={24}
            height={24}
            className="w-7 h-7 object-contain drop-shadow-[0_2px_0_rgba(0,0,0,1)]"
          />

          <span
            className="
            text-[11px] xs:text-sm
            w-full flex items-center justify-center
            font-black tracking-wide
            text-pk-gray-4
            drop-shadow-[0_2px_0_rgba(0,0,0,1)]
          "
          >
            {text}
          </span>
        </div>
      </Button>
    </MotionItem>
  );
};
